import React, { useState, useCallback } from 'react';
import {
  Camera, Plus, Trash2, Copy, Play, Pencil, Check, X,
  Download, ChevronDown, RotateCcw, Video
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Separator } from '../ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useApp, metersToUnit } from '../../context/AppContext';
import { toast } from 'sonner';

const EXPORT_PRESETS = [
  { name: '1080p', width: 1920, height: 1080 },
  { name: '4K', width: 3840, height: 2160 },
  { name: 'Custom', width: 0, height: 0 }
];

const CameraPanel = () => {
  const {
    savedCameras,
    activeCameraId,
    setActiveCameraId,
    selectedCameraId,
    setSelectedCameraId,
    createCamera,
    updateCamera,
    deleteCamera,
    duplicateCamera,
    getCameraStateRef,
    captureScreenshotRef,
    projectSettings
  } = useApp();

  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [exportPreset, setExportPreset] = useState('1080p');
  const [customWidth, setCustomWidth] = useState(1920);
  const [customHeight, setCustomHeight] = useState(1080);
  const [isExporting, setIsExporting] = useState(false);

  const unit = projectSettings?.unit || 'm';

  const handleSaveCurrentView = useCallback(() => {
    if (!getCameraStateRef?.current) {
      toast.error('Camera not ready');
      return;
    }
    const state = getCameraStateRef.current();
    if (!state) {
      toast.error('Could not read camera state');
      return;
    }
    const cam = createCamera(state);
    toast.success(`Saved "${cam.name}"`);
  }, [getCameraStateRef, createCamera]);

  const handleActivate = useCallback((id) => {
    setActiveCameraId(id);
    setSelectedCameraId(id);
  }, [setActiveCameraId, setSelectedCameraId]);

  const handleUpdateFromCurrent = useCallback((id) => {
    if (!getCameraStateRef?.current) return;
    const state = getCameraStateRef.current();
    if (!state) return;
    updateCamera(id, {
      position: state.position,
      target: state.target,
      alpha: state.alpha,
      beta: state.beta,
      radius: state.radius,
      fov: state.fov
    });
    toast.success('Camera updated from current view');
  }, [getCameraStateRef, updateCamera]);

  const handleStartRename = useCallback((id, name) => {
    setRenamingId(id);
    setRenameValue(name);
  }, []);

  const handleFinishRename = useCallback(() => {
    if (renamingId && renameValue.trim()) {
      updateCamera(renamingId, { name: renameValue.trim() });
    }
    setRenamingId(null);
    setRenameValue('');
  }, [renamingId, renameValue, updateCamera]);

  const handleExport = useCallback(async () => {
    if (!captureScreenshotRef?.current) {
      toast.error('Screenshot capture not available');
      return;
    }
    setIsExporting(true);
    try {
      const preset = EXPORT_PRESETS.find(p => p.name === exportPreset);
      const width = exportPreset === 'Custom' ? customWidth : preset.width;
      const height = exportPreset === 'Custom' ? customHeight : preset.height;

      const dataUrl = await captureScreenshotRef.current(width, height);
      if (!dataUrl) {
        toast.error('Export failed');
        return;
      }

      // Download the image
      const link = document.createElement('a');
      link.download = `camera-export-${width}x${height}.png`;
      link.href = dataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(`Exported ${width}x${height} PNG`);
    } catch (err) {
      toast.error('Export failed: ' + err.message);
    } finally {
      setIsExporting(false);
    }
  }, [captureScreenshotRef, exportPreset, customWidth, customHeight]);

  const selectedCam = savedCameras.find(c => c.id === selectedCameraId);

  const formatVal = (meters) => {
    const v = metersToUnit(meters || 0, unit);
    return v.toFixed(2);
  };

  return (
    <div className="space-y-3" data-testid="camera-panel">
      {/* Save Current View */}
      <Button
        className="w-full bg-fuchsia-600 hover:bg-fuchsia-500 text-white text-xs h-8"
        onClick={handleSaveCurrentView}
        data-testid="save-camera-btn"
      >
        <Plus className="w-3 h-3 mr-1" /> Save Current View
      </Button>

      {/* Saved Cameras List */}
      {savedCameras.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-6 text-center">
          <Camera className="w-8 h-8 mb-2 text-zinc-600" />
          <div className="text-xs text-zinc-500">No saved cameras yet</div>
          <div className="text-[10px] text-zinc-600 mt-1">
            Position the viewport, then click "Save Current View"
          </div>
        </div>
      ) : (
        <div className="space-y-1" data-testid="camera-list">
          {savedCameras.map((cam) => (
            <div
              key={cam.id}
              className={`group flex items-center gap-1 px-2 py-1.5 rounded cursor-pointer text-xs transition-colors ${
                selectedCameraId === cam.id
                  ? 'bg-fuchsia-500/20 border border-fuchsia-500/40'
                  : 'bg-zinc-800/50 hover:bg-zinc-700/50 border border-transparent'
              } ${activeCameraId === cam.id ? 'ring-1 ring-fuchsia-400' : ''}`}
              onClick={() => setSelectedCameraId(cam.id)}
              data-testid={`camera-item-${cam.id}`}
            >
              <Camera className="w-3 h-3 text-zinc-400 flex-shrink-0" />
              {renamingId === cam.id ? (
                <form className="flex items-center gap-1 flex-1" onSubmit={(e) => { e.preventDefault(); handleFinishRename(); }}>
                  <Input
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    className="h-5 text-xs bg-zinc-900 px-1 flex-1"
                    autoFocus
                    onBlur={handleFinishRename}
                    data-testid="camera-rename-input"
                  />
                </form>
              ) : (
                <span className="flex-1 truncate text-zinc-300">{cam.name}</span>
              )}
              {activeCameraId === cam.id && (
                <span className="text-[9px] text-fuchsia-400 font-medium flex-shrink-0">ACTIVE</span>
              )}
              {/* Action buttons - always visible */}
              <div className="flex items-center gap-0.5 flex-shrink-0">
                <Button variant="ghost" size="icon" className="h-5 w-5"
                  onClick={(e) => { e.stopPropagation(); handleActivate(cam.id); }}
                  title="Go to this camera"
                  data-testid={`camera-activate-${cam.id}`}
                >
                  <Play className="w-3 h-3 text-green-400" />
                </Button>
                <Button variant="ghost" size="icon" className="h-5 w-5"
                  onClick={(e) => { e.stopPropagation(); handleStartRename(cam.id, cam.name); }}
                  title="Rename"
                  data-testid={`camera-rename-${cam.id}`}
                >
                  <Pencil className="w-3 h-3 text-zinc-400" />
                </Button>
                <Button variant="ghost" size="icon" className="h-5 w-5"
                  onClick={(e) => { e.stopPropagation(); handleUpdateFromCurrent(cam.id); }}
                  title="Update from current view"
                  data-testid={`camera-update-${cam.id}`}
                >
                  <RotateCcw className="w-3 h-3 text-blue-400" />
                </Button>
                <Button variant="ghost" size="icon" className="h-5 w-5"
                  onClick={(e) => { e.stopPropagation(); duplicateCamera(cam.id); }}
                  title="Duplicate"
                  data-testid={`camera-duplicate-${cam.id}`}
                >
                  <Copy className="w-3 h-3 text-zinc-400" />
                </Button>
                <Button variant="ghost" size="icon" className="h-5 w-5"
                  onClick={(e) => { e.stopPropagation(); deleteCamera(cam.id); }}
                  title="Delete"
                  data-testid={`camera-delete-${cam.id}`}
                >
                  <Trash2 className="w-3 h-3 text-red-400" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Selected Camera Details */}
      {selectedCam && (
        <>
          <Separator className="bg-zinc-700" />
          <div className="space-y-2" data-testid="camera-details">
            <div className="properties-section-title flex items-center gap-1">
              <Video className="w-3 h-3" /> Camera Properties
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[10px]">
              <div className="text-zinc-500">Alpha</div>
              <div className="text-zinc-300 text-right">{((selectedCam.alpha || 0) * 180 / Math.PI).toFixed(1)}&deg;</div>
              <div className="text-zinc-500">Beta</div>
              <div className="text-zinc-300 text-right">{((selectedCam.beta || 0) * 180 / Math.PI).toFixed(1)}&deg;</div>
              <div className="text-zinc-500">Radius</div>
              <div className="text-zinc-300 text-right">{formatVal(selectedCam.radius)} {unit}</div>
              <div className="text-zinc-500">FOV</div>
              <div className="text-zinc-300 text-right">{((selectedCam.fov || 0.8) * 180 / Math.PI).toFixed(1)}&deg;</div>
              <div className="text-zinc-500">Target X</div>
              <div className="text-zinc-300 text-right">{formatVal(selectedCam.target?.x)}</div>
              <div className="text-zinc-500">Target Y</div>
              <div className="text-zinc-300 text-right">{formatVal(selectedCam.target?.y)}</div>
              <div className="text-zinc-500">Target Z</div>
              <div className="text-zinc-300 text-right">{formatVal(selectedCam.target?.z)}</div>
            </div>

            {/* Editable FOV slider */}
            <div className="mt-2 space-y-1">
              <Label className="text-[10px] text-zinc-500">Field of View</Label>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min={0.2}
                  max={2.0}
                  step={0.01}
                  value={selectedCam.fov || 0.8}
                  onChange={(e) => updateCamera(selectedCam.id, { fov: parseFloat(e.target.value) })}
                  className="flex-1 h-1 accent-fuchsia-500"
                  data-testid="camera-fov-slider"
                />
                <span className="text-[10px] text-zinc-400 w-10 text-right">
                  {((selectedCam.fov || 0.8) * 180 / Math.PI).toFixed(0)}&deg;
                </span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Export Image Section */}
      <Separator className="bg-zinc-700" />
      <div className="space-y-2" data-testid="camera-export-section">
        <div className="properties-section-title flex items-center gap-1">
          <Download className="w-3 h-3" /> Export Image
        </div>
        <div className="space-y-2">
          <Select value={exportPreset} onValueChange={setExportPreset}>
            <SelectTrigger className="h-7 text-xs" data-testid="export-preset-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EXPORT_PRESETS.map(p => (
                <SelectItem key={p.name} value={p.name}>{p.name}{p.width ? ` (${p.width}x${p.height})` : ''}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {exportPreset === 'Custom' && (
            <div className="flex gap-2">
              <div className="flex-1">
                <Label className="text-[10px] text-zinc-500">Width</Label>
                <Input
                  type="number"
                  value={customWidth}
                  onChange={(e) => setCustomWidth(Math.max(1, parseInt(e.target.value) || 1920))}
                  className="h-7 text-xs bg-zinc-800"
                  data-testid="export-custom-width"
                />
              </div>
              <div className="flex-1">
                <Label className="text-[10px] text-zinc-500">Height</Label>
                <Input
                  type="number"
                  value={customHeight}
                  onChange={(e) => setCustomHeight(Math.max(1, parseInt(e.target.value) || 1080))}
                  className="h-7 text-xs bg-zinc-800"
                  data-testid="export-custom-height"
                />
              </div>
            </div>
          )}
          <Button
            className="w-full bg-emerald-600 hover:bg-emerald-500 text-white text-xs h-8"
            onClick={handleExport}
            disabled={isExporting}
            data-testid="export-image-btn"
          >
            <Download className="w-3 h-3 mr-1" />
            {isExporting ? 'Exporting...' : 'Export PNG'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CameraPanel;
